import { Link, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import type { EmitirGastoComunRequest, Unidad } from '@condo/contracts';
import { EmitirGastoComunRequestSchema } from '@condo/contracts';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useUnidades } from '@/features/unidades/api';
import { useCrearBorrador } from '@/features/gastos-comunes/api';
import { ProrrateoPreview } from '@/features/gastos-comunes/prorrateo-preview';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ClpInput } from '@/components/common/clp-input';
import { formatClp } from '@/lib/format/currency';

const CATEGORIAS = [
  { value: 'administracion', label: 'Administracion' },
  { value: 'mantencion', label: 'Mantencion' },
  { value: 'aseo', label: 'Aseo' },
  { value: 'seguridad', label: 'Seguridad' },
  { value: 'servicios_basicos', label: 'Servicios basicos' },
  { value: 'seguros', label: 'Seguros' },
  { value: 'fondo_reserva', label: 'Fondo de reserva' },
  { value: 'extraordinario', label: 'Extraordinario' },
  { value: 'otros', label: 'Otros' },
] as const;

export function NuevoGastoComunRoute() {
  const { condoId } = useCondoContext();
  const { data: unidadesData, isLoading } = useUnidades(condoId);
  const navigate = useNavigate();

  return (
    <PageContainer>
      <Button variant="ghost" asChild size="sm" className="mb-4">
        <Link to={`/app/condominios/${condoId}/gastos-comunes`}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>
      </Button>
      <PageHeader
        eyebrow="Nueva emision"
        title="Crear borrador de gasto comun"
        subtitle="Ingresa las lineas del periodo. El borrador se puede revisar y editar antes de emitir. El prorrateo por alicuota se calcula automaticamente al emitir."
        ruleSpacing="mb-8"
      />

      {isLoading || !unidadesData ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <CrearBorradorForm
          condoId={condoId}
          unidades={unidadesData.items}
          onCreated={(gastoId) => navigate(`/app/condominios/${condoId}/gastos-comunes/${gastoId}`)}
        />
      )}
    </PageContainer>
  );
}

function CrearBorradorForm({
  condoId,
  unidades,
  onCreated,
}: {
  condoId: string;
  unidades: Unidad[];
  onCreated: (gastoId: string) => void;
}) {
  const mutation = useCrearBorrador(condoId);
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const defaultPeriodo = `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
  const defaultVto = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-10`;

  const form = useForm<EmitirGastoComunRequest>({
    resolver: zodResolver(EmitirGastoComunRequestSchema),
    defaultValues: {
      periodo: defaultPeriodo,
      fechaVencimiento: defaultVto,
      lineas: [{ categoria: 'administracion', concepto: '', monto: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'lineas',
  });

  const lineas = form.watch('lineas');
  const montoTotal = lineas.reduce((s, l) => s + (Number(l.monto) || 0), 0);

  async function onSubmit(values: EmitirGastoComunRequest) {
    try {
      const result = await mutation.mutateAsync(values);
      toast.success('Borrador creado', {
        description: `${lineas.length} linea(s) · ${formatClp(montoTotal)}. Revisa y emite cuando estes listo.`,
      });
      form.reset();
      onCreated(result.gastoId);
    } catch {
      toast.error('No se pudo crear el borrador');
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="periodo">Periodo (YYYY-MM)</Label>
            <Input
              id="periodo"
              className="mt-1.5 tabular"
              placeholder="2026-04"
              {...form.register('periodo')}
            />
            {form.formState.errors.periodo && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {form.formState.errors.periodo.message}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="vto">Fecha de vencimiento</Label>
            <Input
              id="vto"
              type="date"
              className="mt-1.5 tabular"
              {...form.register('fechaVencimiento')}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label>Lineas del gasto comun</Label>
            <span className="text-[0.6875rem] text-[var(--color-ink-faint)]">
              {lineas.length} linea(s)
            </span>
          </div>
          <ul className="mt-2 space-y-2">
            {fields.map((field, index) => (
              <li
                key={field.id}
                className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-3"
              >
                <div className="grid gap-2 md:grid-cols-[170px_1fr_150px_32px]">
                  <Select
                    value={form.watch(`lineas.${index}.categoria`)}
                    onValueChange={(v) =>
                      form.setValue(
                        `lineas.${index}.categoria`,
                        v as EmitirGastoComunRequest['lineas'][number]['categoria'],
                        { shouldValidate: true },
                      )
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Concepto"
                    className="h-9"
                    {...form.register(`lineas.${index}.concepto`)}
                  />
                  <ClpInput
                    value={form.watch(`lineas.${index}.monto`)}
                    onChange={(v) =>
                      form.setValue(`lineas.${index}.monto`, v, {
                        shouldValidate: true,
                      })
                    }
                    className="h-9"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => remove(index)}
                    aria-label="Quitar linea"
                    disabled={fields.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() =>
              append({ categoria: 'otros', concepto: '', monto: 0 })
            }
          >
            <Plus className="h-4 w-4" />
            Agregar linea
          </Button>
        </div>

        <div className="flex items-center justify-between border-t border-[var(--color-hairline)] pt-4">
          <div>
            <p className="eyebrow">Monto total</p>
            <p className="font-display-tight mt-1 text-3xl font-semibold tabular text-[var(--color-primary)]">
              {formatClp(montoTotal)}
            </p>
          </div>
          <Button type="submit" size="lg" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creando...
              </>
            ) : (
              'Crear borrador'
            )}
          </Button>
        </div>
      </div>

      <div>
        <ProrrateoPreview unidades={unidades} montoTotal={montoTotal} />
      </div>
    </form>
  );
}
